import { CLIENT_AUTHENTICATION_SERVICE, ClientAuthenticationService } from "@cervval.js/client-auth";
import { HOURGLASS_SERVICE, HourglassService } from "@cervval.js/client-html5-core/src/services/hourglass.service";
import { DialogSize, showErrorDialog } from "@cervval.js/client-html5-ui/src/components/dialog/dialog";
import { ErrorCodes, RouteErrorJsonResponse, RouteFunctionJsonResponse, isErrorResponse } from "@cervval.js/common-routes";
import { ERROR_MESSAGES } from "@cervval.js/common-routes/src/route.errors.i18n";
import { ERROR_MESSAGE_SERVICE, ErrorMessageService, LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { ErrorMessagesRecord, Injectable, ServiceWithErrorMessages } from "@cervval.js/common/src/dependencies/injectable";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { ErrorCodes as GENERIC_ERROR_CODE } from "@cervval.js/common/src/errors/errors";
import { CLIENT_ROUTES_SERVICE, ClientRouteErrorHandler, ClientRouteErrorHandlerParams, ClientRouteOptions, ClientRoutesService, JsonRequestParameters, RequestParameters } from "./clientRoutes.service";

@Injectable(CLIENT_ROUTES_SERVICE, { registerOnErrorMessageService: true })
export abstract class AbstractClientRoutesService implements ClientRoutesService, ServiceWithErrorMessages {

    protected _options: ClientRouteOptions;

    @Injected(LOG_SERVICE)
    protected readonly _log!: LogService;

    @Injected(CLIENT_AUTHENTICATION_SERVICE, { optional: true })
    protected readonly _login?: ClientAuthenticationService;

    @Injected(ERROR_MESSAGE_SERVICE, { optional: true })
    protected readonly _errorMessages?: ErrorMessageService;

    constructor(options?: ClientRouteOptions) {
        this._options = {
            urlPrefix: options?.urlPrefix ?? "",// no prefix
            timeoutMs: options?.timeoutMs ?? 0,// no timeout
            showHourGlass: options?.showHourGlass ?? true,// standard display
            onError: options?.onError,// no error handling
            withCredentials: options?.withCredentials
        };
    }

    /**
     * @returns service related error messages
     */
    public getServiceErrorMessages(): ErrorMessagesRecord {
        return ERROR_MESSAGES;
    }

    /**
     * Adds the handler in the promise chain but calls the handler function only if previous handlers have returned true (error needs handling)
     */
    protected _chainErrorHandler(errorHandlingPromise: Promise<boolean>, errorHandler: ClientRouteErrorHandler | undefined, error: ClientRouteErrorHandlerParams): Promise<boolean> {
        if (errorHandler) {
            errorHandlingPromise = errorHandlingPromise.then((errorNeedsHandling) => {
                if (errorNeedsHandling) {
                    return errorHandler(error);
                }
                return false;// error already handled
            });
        }
        return errorHandlingPromise;
    }

    /**
     * This function is called when the server returned en error (an error response status or an error code in the response body)
     */
    protected _handleClientRouteError<R>(responseStatus: number, request: RequestParameters, requestOptions: ClientRouteOptions | undefined, err: RouteErrorJsonResponse | undefined): Promise<R | undefined> {
        const error: ClientRouteErrorHandlerParams<R> = {
            responseStatus,// HTTP or app status code
            request,// request that errored
            result: undefined,// if error is handled, result will be sent as the request promise result
            err // error returned by the server (may be undefined if server did not reply)
        };
        let p: Promise<boolean> = Promise.resolve(true);// true => error needs to be handled
        p = this._chainErrorHandler(p, requestOptions?.onError, error);// call error handler provided when request was emitted
        p = this._chainErrorHandler(p, this._options?.onError, error);// call default error handler

        if (responseStatus === ErrorCodes.UNAUTHORIZED || err?.code === ErrorCodes.UNAUTHORIZED) {
            // responseStatus when server directly replied with a http error code
            // err.code when our code server side sent the 401 error

            // on UNAUTHORIZED error we always redirect to login even if error was handled
            p = p.catch((err) => {
                // we don't care if there was an error => we redirect
            }).then(() => {
                if (!this._login) {
                    this._log.error("Server requests authentication for " + request.url + " but there is no " + CLIENT_AUTHENTICATION_SERVICE + " registered");
                }
                return this._login?.redirectToLogin();
            }).then(() => false);// error is handled
        }

        return p.then((errorNeedsHandling) => {
            if (errorNeedsHandling) {
                // ensure we always reject a RouteErrorJsonResponse
                const toReject: RouteErrorJsonResponse = err ?? {
                    code: responseStatus < ErrorCodes.BAD_REQUEST ? ErrorCodes.INTERNAL_ERROR : responseStatus,// if responseStatus is a 'success', we consider it as an internal error
                    error: true
                };

                if (this._shouldDisplayErrorDialog(requestOptions)) {
                    showErrorDialog({
                        error: toReject
                    }, {
                        size: DialogSize.EXTRA_LARGE
                    });
                    toReject.noDialog = true;// do not show twice
                }

                return Promise.reject(toReject);
            }
            return error.result;// error was handled, we return the result
        });
    }

    /**
     * Executes the request (no error handling)
     */
    protected abstract _callImpl(request: RequestParameters, options?: { timeoutMs?: number, urlPrefix?: string }): Promise<{ responseStatus: number, reply: RouteFunctionJsonResponse | RouteErrorJsonResponse | undefined }>;


    /**
     * @returns false if passed options or this._options ask not to log errors (options is first over this._options)
     */
    protected _shouldLogError(options?: ClientRouteOptions): boolean {
        if (options?.doNotLogErrors === true) {
            return false;// request options asked not to log errors
        }
        if (options?.doNotLogErrors !== false && this._options?.doNotLogErrors === true) {
            return false;// global options asked not to log errors (and request options did not override this)
        }
        return true;
    }

    /**
     * @returns false if passed options or this._options ask not to display errors (options is first over this._options)
     */
    protected _shouldDisplayErrorDialog(options?: ClientRouteOptions): boolean {
        if (options?.doNotShowDialogOnErrors === true) {
            return false;// request options asked not to display errors
        }
        if (options?.doNotShowDialogOnErrors !== false && this._options?.doNotShowDialogOnErrors === true) {
            return false;// global options asked not to display errors (and request options did not override this)
        }
        return true;
    }

    /**
     * Execute a request on the server and handles errors
     * Use this function if you need to control th contentType or request type. Most of the time, you should use callJson.
     */
    public call<R = unknown>(request: RequestParameters, options?: ClientRouteOptions): Promise<R | undefined> {
        let p = this._callImpl(request, options).then((response) => {
            const reply = response.reply;
            if (response.responseStatus === 200 && response.reply === null) {
                return Promise.resolve(undefined); // Handle redirection to an external server (not in CervvalJs)
            }
            if (isErrorResponse(reply)) {
                return this._handleClientRouteError<R>(response.responseStatus, request, options, reply);
            }
            else if (reply?.code === GENERIC_ERROR_CODE.ABORT_ERROR_CODE) {
                return Promise.reject(reply.code);
            }
            const okReply = reply as RouteFunctionJsonResponse;
            return Promise.resolve(okReply.result as R);
        });
        const showHourGlassOptions = options?.showHourGlass ?? this._options.showHourGlass;
        if (showHourGlassOptions) {
            const hourGlassService = injector.find<HourglassService<unknown>>(HOURGLASS_SERVICE);
            if (hourGlassService) { // If hour glass service is not registered, skip the option
                const _impl = p;
                const hourglassOptions = typeof showHourGlassOptions !== "boolean" ? showHourGlassOptions : undefined;
                p = hourGlassService.encapsulate(() => _impl, { showOnlyIfLongerThanMs: 400, ...hourglassOptions });
            }
        }
        if (!this._shouldLogError(options)) {
            return p;// options asked not to log errors
        }
        return p.catch((err) => {
            if (err !== GENERIC_ERROR_CODE.ABORT_ERROR_CODE) { this._log.error(err); } // error was not handled
            return Promise.reject(err);
        });
    }

    /**
     * Execute a request on the server with a JSON content type and the default method (eg POST)
     */
    public callJson<R = unknown>(request: JsonRequestParameters, options?: ClientRouteOptions): Promise<R | undefined> {
        return this.call({
            url: request.url,
            data: request.params == null ? null : JSON.stringify(request.params),
            method: options?.method ?? "post",
            contentType: "application/json"
        }, options);
    }

}